import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('siddhi_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async (retryOnTransient = true) => {
    let stopLoading = true;
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Only log the user out if the token is actually invalid (401/403).
      // Network errors, timeouts, or upstream 5xx (e.g. proxy killed a long-running
      // background request) should NOT trigger a logout — that's a UX bug.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        console.error('Auth token invalid, logging out');
        logout();
      } else if (retryOnTransient) {
        console.warn('fetchUser transient error, retrying in 2s:', error?.message || error);
        stopLoading = false;  // keep loading=true so ProtectedRoute waits
        setTimeout(() => fetchUser(false), 2000);
      } else {
        console.warn('fetchUser failed twice (keeping session token):', error?.message || error);
        // Token still in storage; user can retry by refreshing later.
      }
    } finally {
      if (stopLoading) setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('siddhi_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, 
      password, 
      name, 
      role 
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('siddhi_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('siddhi_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin';
  const isProduction = () => ['production', 'admin', 'super_admin'].includes(user?.role);
  const isSales = () => ['sales', 'admin', 'super_admin'].includes(user?.role);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      isAdmin,
      isProduction,
      isSales
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
